// src/components/Analytics/TrainingAnalytics.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "../../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { DateTime } from "luxon";
import "../../styles/Analytics.css";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

// ─────────────────────────── Date range helpers
const formatMDY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
};

function buildRangeFromPreset(preset) {
  const end = DateTime.now().startOf("day");
  let start = end;

  switch (preset) {
    case "LAST_7":
      start = end.minus({ days: 6 });
      break;
    case "LAST_30":
      start = end.minus({ days: 29 });
      break;
    case "LAST_90":
      start = end.minus({ days: 89 });
      break;
    case "MTD":
      start = end.startOf("month");
      break;
    case "YTD":
      start = end.startOf("year");
      break;
    default:
      start = end.minus({ days: 29 });
  }

  return { startISO: start.toISODate(), endISO: end.toISODate() };
}

function inISODateRange(iso, startISO, endISO) {
  if (!iso || !startISO || !endISO) return false;
  const d = DateTime.fromISO(iso).startOf("day");
  const s = DateTime.fromISO(startISO).startOf("day");
  const e = DateTime.fromISO(endISO).startOf("day");
  if (!d.isValid || !s.isValid || !e.isValid) return false;
  const ms = d.toMillis();
  return ms >= s.toMillis() && ms <= e.toMillis();
}

// ✅ Resolve a "progress date" for filtering (prefer assigned date)
function getProgressDateISO(p) {
  if (!p) return null;

  // Prefer assignment date fields if you have them
  const candidates = [
    p.assignedAt,
    p.assignedOn,
    p.assignedDate,
    p.createdAt,
    p.createdOn,
    p.updatedAt,
    p.deadline,
    p.deadlineISO,
    p.dueDate,
  ].filter(Boolean);

  for (const v of candidates) {
    try {
      if (typeof v === "string") {
        const dt = DateTime.fromISO(v);
        if (dt.isValid) return dt.toISODate();
      }
      if (typeof v?.toDate === "function") {
        const dt = DateTime.fromJSDate(v.toDate());
        if (dt.isValid) return dt.toISODate();
      }
      if (typeof v === "number") {
        const dt = DateTime.fromMillis(v);
        if (dt.isValid) return dt.toISODate();
      }
      // last resort
      const dt = DateTime.fromJSDate(new Date(v));
      if (dt.isValid) return dt.toISODate();
    } catch {
      // ignore
    }
  }

  return null;
}

export default function TrainingAnalytics({ companyId, employeeId = null }) {
  const [courses, setCourses] = useState([]);
  const [trainingProgress, setTrainingProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isDrilldown = !!employeeId;

  // ✅ Date range state
  const [rangePreset, setRangePreset] = useState("LAST_30");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  // Guard (no early returns before hooks)
  useEffect(() => {
    if (!companyId) {
      setError("Company not loaded.");
      setLoading(false);
    } else {
      setError("");
    }
  }, [companyId]);

  // ✅ Initialize default range from preset
  useEffect(() => {
    const todayISO = DateTime.now().toISODate();

    // CUSTOM: only fill blanks, don't overwrite user selections
    if (rangePreset === "CUSTOM") {
      if (!rangeStart) setRangeStart(todayISO);
      if (!rangeEnd) setRangeEnd(todayISO);
      return;
    }

    const { startISO, endISO } = buildRangeFromPreset(rangePreset);

    // ✅ only set if changed (prevents re-render loops)
    if (rangeStart !== startISO) setRangeStart(startISO);
    if (rangeEnd !== endISO) setRangeEnd(endISO);
  }, [rangePreset, rangeStart, rangeEnd]); // intentionally not depending on rangeStart/rangeEnd

  // ✅ Guard: keep start <= end
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const s = DateTime.fromISO(rangeStart);
    const e = DateTime.fromISO(rangeEnd);
    if (s.isValid && e.isValid && s > e) setRangeEnd(rangeStart);
  }, [rangeStart, rangeEnd]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) return;

      try {
        setLoading(true);
        setError("");

        // Courses
        const courseSnap = await getDocs(
          collection(db, `companies/${companyId}/training_courses`)
        );
        const courseData = courseSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        // Progress
        let all = [];

        if (employeeId) {
          const progressSnap = await getDocs(
            collection(
              db,
              `companies/${companyId}/employees/${employeeId}/training_progress`
            )
          );
          all = progressSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            employeeId,
          }));
        } else {
          const employeeSnap = await getDocs(
            collection(db, `companies/${companyId}/employees`)
          );

          for (const emp of employeeSnap.docs) {
            const progressSnap = await getDocs(
              collection(
                db,
                `companies/${companyId}/employees/${emp.id}/training_progress`
              )
            );
            const progressData = progressSnap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              employeeId: emp.id,
            }));
            all = all.concat(progressData);
          }
        }

        setCourses(courseData);
        setTrainingProgress(all);
      } catch (e) {
        console.error(e);
        setError("Failed to load training analytics.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, employeeId]);

  // ✅ stable helpers
  const statusKey = useCallback(
    (v) => (v || "Unknown").toString().toLowerCase(),
    []
  );

  const isOverdue = useCallback(
    (p) => {
      const deadlineRaw = p.deadline || p.deadlineISO || p.dueDate || null;
      if (!deadlineRaw) return false;

      try {
        let deadline;
        if (typeof deadlineRaw === "string") deadline = new Date(deadlineRaw);
        else if (deadlineRaw?.toDate) deadline = deadlineRaw.toDate();
        else deadline = new Date(deadlineRaw);

        if (Number.isNaN(deadline.getTime())) return false;

        return statusKey(p.status) !== "completed" && deadline < new Date();
      } catch {
        return false;
      }
    },
    [statusKey]
  );

  // ✅ Date-filtered progress
  const scopedProgress = useMemo(() => {
    if (!rangeStart || !rangeEnd) return trainingProgress;

    return trainingProgress.filter((p) => {
      const iso = getProgressDateISO(p);
      return iso ? inISODateRange(iso, rangeStart, rangeEnd) : false;
    });
  }, [trainingProgress, rangeStart, rangeEnd]);

  // ────────── KPIs (use scopedProgress)
  const totalAssigned = useMemo(() => {
    // In drilldown, progress docs are the assignment list.
    if (isDrilldown) return scopedProgress.length;

    // Company-wide: best "assigned" metric that respects the date range
    // is simply the number of progress records in range.
    return scopedProgress.length;
  }, [isDrilldown, scopedProgress]);

  const totalCompleted = useMemo(
    () =>
      scopedProgress.filter((p) => statusKey(p.status) === "completed").length,
    [scopedProgress, statusKey]
  );

  const completionRate = useMemo(() => {
    const denom = totalAssigned || 0;
    if (!denom) return "0.0";
    return ((totalCompleted / denom) * 100).toFixed(1);
  }, [totalAssigned, totalCompleted]);

  const overdueCount = useMemo(
    () => scopedProgress.filter((p) => isOverdue(p)).length,
    [scopedProgress, isOverdue]
  );

  // ────────── Aggregations (use scopedProgress)
  const statusBreakdown = useMemo(() => {
    return scopedProgress.reduce((acc, p) => {
      const s = statusKey(p.status);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [scopedProgress, statusKey]);

  // ✅ Most assigned courses (company-wide only, date-range aware via progress records)
  const mostAssignedCourses = useMemo(() => {
    if (isDrilldown) return {};
    return scopedProgress.reduce((acc, p) => {
      const title =
        p.courseTitle ||
        p.title ||
        (p.courseId
          ? courses.find((c) => c.id === p.courseId)?.courseTitle ||
            courses.find((c) => c.id === p.courseId)?.title
          : null) ||
        "(Untitled)";
      acc[title] = (acc[title] || 0) + 1;
      return acc;
    }, {});
  }, [isDrilldown, scopedProgress, courses]);

  // ────────── Charts
  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { position: "right" },
      tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}` } },
    },
    cutout: "58%",
  };

  const statusData = useMemo(
    () => ({
      labels: Object.keys(statusBreakdown).map(
        (s) => s[0].toUpperCase() + s.slice(1)
      ),
      datasets: [
        {
          data: Object.values(statusBreakdown),
          backgroundColor: [
            "#f59e0b",
            "#22c55e",
            "#ef4444",
            "#6366f1",
            "#94a3b8",
          ],
          borderWidth: 0,
        },
      ],
    }),
    [statusBreakdown]
  );

  const courseLabels = useMemo(() => Object.keys(mostAssignedCourses), [
    mostAssignedCourses,
  ]);
  const courseValues = useMemo(() => Object.values(mostAssignedCourses), [
    mostAssignedCourses,
  ]);

  const courseBarData = useMemo(
    () => ({
      labels: courseLabels,
      datasets: [
        {
          label: "Assignments",
          data: courseValues,
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#36A2EB";
            const grad = g.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0
            );
            grad.addColorStop(0, "rgba(54, 162, 235, 0.95)");
            grad.addColorStop(1, "rgba(245, 158, 11, 0.55)");
            return grad;
          },
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 42,
        },
      ],
    }),
    [courseLabels, courseValues]
  );

  const courseBarOptions = useMemo(
    () => ({
      responsive: true,
      indexAxis: courseLabels.length <= 8 ? "y" : "x",
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => `${ctx.raw} assignments` } },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { precision: 0 },
          grid: { color: "rgba(0,0,0,0.08)" },
        },
        y: {
          ticks: {
            autoSkip: false,
            callback: (v, i) => {
              const label = courseLabels[i] || "";
              return label.length > 24 ? label.slice(0, 24) + "…" : label;
            },
          },
          grid: { display: false },
        },
      },
    }),
    [courseLabels]
  );

  // ────────── UI
  if (!companyId) return <p>Loading...</p>;
  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "crimson" }}>{error}</p>;

  return (
    <div className="management-analytics-wrapper">
      {/* ✅ Date range controls */}
      <div className="analytics-rangebar">
        <div className="analytics-filter">
          <label className="analytics-filter-label">Date Range</label>
          <select
            className="analytics-dropdown"
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value)}
          >
            <option value="LAST_7">Last 7 days</option>
            <option value="LAST_30">Last 30 days</option>
            <option value="LAST_90">Last 90 days</option>
            <option value="MTD">Month to date</option>
            <option value="YTD">Year to date</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">Start</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeStart}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeStart(e.target.value);
            }}
          />
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">End</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeEnd}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeEnd(e.target.value);
            }}
          />
        </div>

        <div className="analytics-range-pill">
          {rangeStart && rangeEnd
            ? `${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)}`
            : "—"}
        </div>
      </div>

      <div className="analytics-hint">
        Showing Training analytics for{" "}
        <b>{isDrilldown ? "1 employee" : "all employees"}</b>.
      </div>

      <div className="kpi-grid">
        <div className="kpi-card accent-blue">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Total Assigned</span>
          </div>
          <div className="kpi-value">{totalAssigned}</div>
          <div className="kpi-sub">
            {isDrilldown ? "This employee" : "Assignments in range"}
          </div>
        </div>

        <div className="kpi-card accent-green">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Completion Rate</span>
          </div>
          <div className="kpi-value">{completionRate}%</div>
          <div className="kpi-sub">Completed / Assigned</div>
        </div>

        <div className="kpi-card accent-red">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Overdue Trainings</span>
          </div>
          <div className="kpi-value">{overdueCount}</div>
          <div className="kpi-sub">Past deadline</div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Training Status Distribution</h3>
          <Pie data={statusData} options={pieOptions} />
        </div>

        {!isDrilldown && (
          <div className="chart-container" style={{ minHeight: 420 }}>
            <h3>Most Assigned Courses (In Range)</h3>
            <Bar data={courseBarData} options={courseBarOptions} />
          </div>
        )}
      </div>
    </div>
  );
}
